import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';

export type OnboardingEntity = 'companies' | 'sites' | 'users';

export interface OnboardingFieldSchema {
  key: string;
  required?: boolean;
  type?: string;
  allowed_values?: Array<string | number>;
  allowed_map?: Array<{ value: string | number; label: string }>;
  multi_value?: boolean;
  multi_value_separator?: string;
  example?: string | number | boolean;
  description?: string;
}

export interface OnboardingEntitySchema {
  fields: OnboardingFieldSchema[];
  demo_row: Record<string, any>;
}

export type OnboardingSchemaMap = Record<OnboardingEntity, OnboardingEntitySchema>;

const FALLBACK_SCHEMA: OnboardingSchemaMap = {
  companies: {
    fields: [
      { key: 'company_name', required: true, type: 'string', example: 'Demo Solar Private Limited' },
      { key: 'industry_type', type: 'string', allowed_values: ['Renewable Energy', 'Manufacturing', 'Information Technology', 'Logistics'], example: 'Renewable Energy' },
      { key: 'country', type: 'string', example: 'India' },
      { key: 'location', type: 'string', example: 'Noida, Uttar Pradesh' },
      { key: 'timezone', type: 'string', example: 'Asia/Kolkata' },
      { key: 'currency', type: 'string', example: 'INR' },
      { key: 'primary_contact_name', type: 'string', example: 'Amit Sharma' },
      { key: 'primary_contact_email', type: 'email', example: 'amit.sharma@example.com' },
      { key: 'primary_contact_phone', type: 'string', multi_value: true, multi_value_separator: '|', example: '+919999999999|+918888888888' },
      { key: 'subscription_plan', type: 'string', allowed_values: ['basic', 'premium', 'enterprise'], example: 'basic' },
      { key: 'max_sites', type: 'number', example: 5 },
      { key: 'max_users', type: 'number', example: 25 },
      { key: 'is_trial', type: 'boolean', allowed_values: ['true', 'false'], example: 'true' },
    ],
    demo_row: {
      company_name: 'Demo Solar Private Limited',
      industry_type: 'Renewable Energy',
      country: 'India',
      location: 'Noida, Uttar Pradesh',
      timezone: 'Asia/Kolkata',
      currency: 'INR',
      primary_contact_name: 'Amit Sharma',
      primary_contact_email: 'amit.sharma@example.com',
      primary_contact_phone: '+919999999999|+918888888888',
      subscription_plan: 'basic',
      max_sites: 5,
      max_users: 25,
      is_trial: 'true',
    },
  },
  sites: {
    fields: [
      { key: 'company_name', type: 'string', example: 'Demo Solar Private Limited' },
      { key: 'site_name', required: true, type: 'string', example: 'Demo Solar Site 01' },
      { key: 'site_type', type: 'string', example: 'Solar' },
      { key: 'location', type: 'string', example: 'Jaipur, Rajasthan' },
      { key: 'capacity_mw', type: 'number', example: 25.5 },
      { key: 'capacity_mwp', type: 'number', example: 32.0 },
      { key: 'description', type: 'string', example: 'North block utility scale plant' },
      { key: 'local_time_zone', type: 'string', example: 'Asia/Kolkata' },
      { key: 'timezone_preference', type: 'string', example: 'Asia/Kolkata' },
      { key: 'latitude', type: 'number', example: 26.9124 },
      { key: 'longitude', type: 'number', example: 75.7873 },
      { key: 'altitude', type: 'number', example: 431.2 },
      { key: 'ppa_tariff', type: 'number', example: 3.15 },
      { key: 'plant_code', type: 'number', allowed_values: [0, 1, 2, 3, 4], example: 0 },
      { key: 'currency', type: 'string', example: 'INR' },
      { key: 'plant_commissioning_date', type: 'date', example: '2026-01-15' },
      { key: 'data_resolution', type: 'string', allowed_values: ['1m', '2m', '5m', '10m', '15m', '30m', '1h', '2h', '4h', '6h', '12h', '1d'], example: '5m' },
      { key: 'site_manager_name', type: 'string', example: 'Ravi Verma' },
      { key: 'site_manager_email', type: 'email', example: 'ravi.verma@example.com' },
      { key: 'site_manager_phone_no', type: 'string', example: '+919111111111' },
    ],
    demo_row: {
      company_name: 'Demo Solar Private Limited',
      site_name: 'Demo Solar Site 01',
      site_type: 'Solar',
      location: 'Jaipur, Rajasthan',
      capacity_mw: 25.5,
      capacity_mwp: 32.0,
      description: 'North block utility scale plant',
      local_time_zone: 'Asia/Kolkata',
      timezone_preference: 'Asia/Kolkata',
      latitude: 26.9124,
      longitude: 75.7873,
      altitude: 431.2,
      ppa_tariff: 3.15,
      plant_code: 0,
      currency: 'INR',
      plant_commissioning_date: '2026-01-15',
      data_resolution: '5m',
      site_manager_name: 'Ravi Verma',
      site_manager_email: 'ravi.verma@example.com',
      site_manager_phone_no: '+919111111111',
    },
  },
  users: {
    fields: [
      { key: 'company_name', type: 'string', example: 'Demo Solar Private Limited' },
      { key: 'site_name', type: 'string', example: 'Demo Solar Site 01' },
      { key: 'username', required: true, type: 'string', example: 'ravi.manager' },
      { key: 'email', required: true, type: 'email', example: 'ravi.manager@example.com' },
      { key: 'first_name', required: true, type: 'string', example: 'Ravi' },
      { key: 'last_name', required: true, type: 'string', example: 'Verma' },
      { key: 'password', required: true, type: 'string', example: 'Demo@12345' },
      { key: 'role', required: true, type: 'string', allowed_values: ['system_admin', 'ho_admin', 'site_admin', 'general_user'], example: 'general_user' },
      { key: 'country_code', type: 'string', example: '+91' },
      { key: 'phone_number', type: 'string', example: '9876543210' },
    ],
    demo_row: {
      company_name: 'Demo Solar Private Limited',
      site_name: 'Demo Solar Site 01',
      username: 'ravi.manager',
      email: 'ravi.manager@example.com',
      first_name: 'Ravi',
      last_name: 'Verma',
      password: 'Demo@12345',
      role: 'general_user',
      country_code: '+91',
      phone_number: '9876543210',
    },
  },
};

const normalizeHeader = (key: string): string =>
  String(key || '')
    .trim()
    .toLowerCase()
    .replace(/[\s\-\/]+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

export const getFallbackOnboardingSchema = (): OnboardingSchemaMap => FALLBACK_SCHEMA;

export const getEntitySchema = (
  entity: OnboardingEntity,
  schema?: Partial<OnboardingSchemaMap> | null,
): OnboardingEntitySchema => {
  return (schema?.[entity] as OnboardingEntitySchema) || FALLBACK_SCHEMA[entity];
};

export const pickRowValue = (row: Record<string, any>, aliases: string[]): any => {
  for (const alias of aliases) {
    if (Object.prototype.hasOwnProperty.call(row, alias)) {
      const value = row[alias];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return value;
      }
    }
  }
  return '';
};

export const parseBooleanCell = (value: any, defaultValue = true): boolean => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return defaultValue;
  }

  const normalized = String(value).trim().toLowerCase();
  if (['true', '1', 'yes', 'y', 'active', 'enabled'].includes(normalized)) return true;
  if (['false', '0', 'no', 'n', 'inactive', 'disabled'].includes(normalized)) return false;
  return defaultValue;
};

export const parseNumberCell = (value: any, fallback: number | null = null): number | null => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return fallback;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const validateOnboardingRow = (
  entity: OnboardingEntity,
  row: Record<string, any>,
  schema?: Partial<OnboardingSchemaMap> | null,
): string[] => {
  const errors: string[] = [];
  const entitySchema = getEntitySchema(entity, schema);

  entitySchema.fields.forEach((field) => {
    const rawValue = row[field.key];
    const trimmed = String(rawValue ?? '').trim();

    if (field.required && !trimmed) {
      errors.push(`${field.key} is required`);
      return;
    }

    if (!trimmed) {
      return;
    }

    if (field.allowed_values && field.allowed_values.length > 0) {
      const allowed = field.allowed_values.map((item) => String(item).toLowerCase());
      if (!allowed.includes(trimmed.toLowerCase())) {
        errors.push(`${field.key} must be one of: ${field.allowed_values.join(', ')}`);
      }
    }
  });

  return errors;
};

export const isDemoTemplateRow = (
  entity: OnboardingEntity,
  row: Record<string, any>,
  schema?: Partial<OnboardingSchemaMap> | null,
): boolean => {
  const entitySchema = getEntitySchema(entity, schema);
  const demoRow = entitySchema.demo_row || {};
  const keys = entitySchema.fields.map((field) => field.key);

  return keys.every((key) => {
    const currentValue = String(row[key] ?? '').trim().toLowerCase();
    const demoValue = String(demoRow[key] ?? '').trim().toLowerCase();
    return currentValue === demoValue;
  });
};

export const parseExcelRows = async (file: File): Promise<Array<Record<string, any>>> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) {
    return [];
  }

  const sheet = workbook.Sheets[firstSheetName];
  const rows = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, {
    defval: '',
    raw: false,
    blankrows: false,
  });

  return rows.map((row) => {
    const normalized: Record<string, any> = {};
    Object.entries(row || {}).forEach(([key, value]) => {
      normalized[normalizeHeader(key)] = value;
    });
    return normalized;
  });
};

// Helper function to get excel column letter (e.g. 1 -> A, 27 -> AA)
function getColLetter(colIdx: number): string {
  let temp = colIdx;
  let letter = '';
  while (temp > 0) {
    const temp2 = (temp - 1) % 26;
    letter = String.fromCharCode(65 + temp2) + letter;
    temp = Math.floor((temp - temp2) / 26);
  }
  return letter;
}

export const downloadOnboardingTemplate = async (
  entity: OnboardingEntity,
  schema?: Partial<OnboardingSchemaMap> | null,
): Promise<void> => {
  const entitySchema = getEntitySchema(entity, schema);
  const headers = entitySchema.fields.map((field) => field.key);
  const demoRow = entitySchema.demo_row || {};

  const workbook = new ExcelJS.Workbook();
  const templateSheet = workbook.addWorksheet('Template');

  // Define columns
  templateSheet.columns = headers.map((header) => ({
    header: header,
    key: header,
    width: 22,
  }));

  // Populate row 2 with demo values
  const demoData = headers.reduce((acc, key) => {
    acc[key] = demoRow[key] ?? '';
    return acc;
  }, {} as Record<string, any>);
  templateSheet.addRow(demoData);

  // Style header row
  const headerRow = templateSheet.getRow(1);
  headerRow.font = { name: 'Segoe UI', size: 11, bold: true, color: { argb: 'FFFFFF' } };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: '004B87' }, // Corporate Dark Blue theme
  };
  headerRow.height = 25;
  headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

  // Style demo data row (gray bolded)
  const demoDataRow = templateSheet.getRow(2);
  demoDataRow.font = { name: 'Segoe UI', size: 10, bold: true, color: { argb: '718096' } };

  // Create DropdownLists sheet with column values
  const dropdownLists: Record<string, string[]> = {
    Booleans: ['true', 'false'],
    Industries: ['Renewable Energy', 'Manufacturing', 'Information Technology', 'Logistics', 'Healthcare', 'Finance'],
    SubscriptionPlans: ['basic', 'premium', 'enterprise'],
    SiteTypes: ['Solar', 'Wind', 'Hydro', 'Biogas', 'Thermal', 'Hybrid', 'Battery', 'ACSEPL', 'WTC', 'LAP', 'AXON'],
    PlantCodes: ['0', '1', '2', '3', '4'],
    DataResolutions: ['1m', '2m', '5m', '10m', '15m', '30m', '1h', '2h', '4h', '6h', '12h', '1d'],
    Currencies: ['INR', 'USD', 'EUR', 'GBP', 'JPY', 'AUD', 'CAD'],
    Roles: ['system_admin', 'ho_admin', 'site_admin', 'general_user', 'viewer', 'data_entry'],
    Countries: [
      'India', 'USA', 'UK', 'Germany', 'France', 'Japan', 'China', 'Australia',
      'Canada', 'Brazil', 'Mexico', 'Spain', 'Italy', 'Netherlands', 'Sweden'
    ],
    Timezones: [
      'Asia/Kolkata', 'Asia/Singapore', 'Asia/Bangkok', 'Asia/Jakarta',
      'UTC', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
      'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
      'Australia/Sydney', 'Australia/Melbourne', 'Pacific/Auckland'
    ],
  };

  const listSheet = workbook.addWorksheet('DropdownLists');
  const listKeys = Object.keys(dropdownLists);

  listKeys.forEach((key, colIdx) => {
    const colNum = colIdx + 1;
    listSheet.getCell(1, colNum).value = key;
    dropdownLists[key].forEach((val, rowIdx) => {
      listSheet.getCell(rowIdx + 2, colNum).value = val;
    });
  });

  // Hide the lists sheet
  listSheet.state = 'hidden';

  // Apply cell validations to Template sheet rows 2-200
  entitySchema.fields.forEach((field, colIdx) => {
    const colNumber = colIdx + 1;
    let listKey = '';

    if (field.key === 'industry_type') listKey = 'Industries';
    else if (field.key === 'country') listKey = 'Countries';
    else if (field.key === 'timezone' || field.key === 'local_time_zone' || field.key === 'timezone_preference') listKey = 'Timezones';
    else if (field.key === 'currency') listKey = 'Currencies';
    else if (field.key === 'subscription_plan') listKey = 'SubscriptionPlans';
    else if (field.key === 'is_active' || field.key === 'is_trial') listKey = 'Booleans';
    else if (field.key === 'site_type') listKey = 'SiteTypes';
    else if (field.key === 'plant_code') listKey = 'PlantCodes';
    else if (field.key === 'data_resolution') listKey = 'DataResolutions';
    else if (field.key === 'role') listKey = 'Roles';

    if (listKey) {
      const listColIdx = listKeys.indexOf(listKey) + 1;
      const listColLetter = getColLetter(listColIdx);
      const listLength = dropdownLists[listKey].length;
      const formula = `DropdownLists!$${listColLetter}$2:$${listColLetter}$${listLength + 1}`;

      for (let r = 2; r <= 200; r++) {
        const cell = templateSheet.getCell(r, colNumber);
        cell.dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [formula],
          showErrorMessage: true,
          errorTitle: 'Invalid Input',
          error: `Please select a valid option from the dropdown list for ${field.key} to prevent spelling mismatches.`,
        };
        // Highlight dropdown columns with an elegant premium soft yellow background
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FEF3C7' },
        };
        // Add subtle light gray borders to ensure visual structure is retained
        cell.border = {
          top: { style: 'thin', color: { argb: 'E2E8F0' } },
          left: { style: 'thin', color: { argb: 'E2E8F0' } },
          bottom: { style: 'thin', color: { argb: 'E2E8F0' } },
          right: { style: 'thin', color: { argb: 'E2E8F0' } },
        };
      }
    }
  });

  // Reference Sheet
  const referenceSheet = workbook.addWorksheet('Reference');
  referenceSheet.columns = [
    { header: 'Field Name', key: 'field', width: 25 },
    { header: 'Required', key: 'required', width: 12 },
    { header: 'Data Type', key: 'type', width: 12 },
    { header: 'Allowed Values', key: 'allowed_values', width: 40 },
    { header: 'Example Value', key: 'example', width: 25 },
    { header: 'Multi-Value Support', key: 'multi_value_format', width: 20 },
    { header: 'Description / Notes', key: 'notes', width: 45 },
  ];

  entitySchema.fields.forEach((field) => {
    const allowedFromMap = Array.isArray(field.allowed_map)
      ? field.allowed_map.map((item) => `${item.value}=${item.label}`).join(' | ')
      : '';
    const allowedValues = Array.isArray(field.allowed_values) ? field.allowed_values.join(', ') : '';

    referenceSheet.addRow({
      field: field.key,
      required: field.required ? 'Yes' : 'No',
      type: field.type || 'string',
      allowed_values: allowedFromMap || allowedValues || 'Any',
      example: field.example ?? '',
      multi_value_format: field.multi_value
        ? `Use separator "${field.multi_value_separator || '|'}"`
        : '-',
      notes: field.description || '',
    });
  });

  const refHeaderRow = referenceSheet.getRow(1);
  refHeaderRow.font = { name: 'Segoe UI', size: 11, bold: true, color: { argb: 'FFFFFF' } };
  refHeaderRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: '4A5568' },
  };
  refHeaderRow.height = 25;
  refHeaderRow.alignment = { vertical: 'middle', horizontal: 'center' };

  // Write file & trigger download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `axonmetric_${entity}_template.xlsx`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
};

export const getOnboardingTemplateColumns = (
  entity: OnboardingEntity,
  schema?: Partial<OnboardingSchemaMap> | null,
): string[] => {
  return getEntitySchema(entity, schema).fields.map((field) => field.key);
};
